import React, { useEffect, useMemo, useState } from 'react';
import {
  getDettesFournisseurs,
  getFournisseurs,
  payerCommandeFournisseur,
  getBonPaiementFournisseurPDF
} from '../services/api';
import {
  CreditCard, AlertCircle, CheckCircle, X, Check, Loader,
  Filter, RotateCcw, Wallet, TrendingDown, Clock, DollarSign,
  ChevronDown, ChevronUp, FileText, Download
} from 'lucide-react';
import './DettesFournisseurs.css';

const fmt = (n) => `${Number(n || 0).toLocaleString('fr-FR')} XOF`;
const fdate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

const STATUT = {
  payee:     { label: 'Payée',     color: '#16a34a', bg: '#dcfce7' },
  partielle: { label: 'Partielle', color: '#d97706', bg: '#fef3c7' },
  non_payee: { label: 'Non payée', color: '#dc2626', bg: '#fee2e2' },
};

const MODES = {
  especes:       'Espèces',
  wave:          'Wave',
  orange_money:  'Orange Money',
  carte_bancaire:'Carte bancaire',
  virement:      'Virement',
  cheque:        'Chèque',
  autre:         'Autre',
};

const StatutBadge = ({ statut }) => {
  const s = STATUT[statut] || STATUT.non_payee;
  return (
    <span className="df-statut-badge" style={{ background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
};

export default function DettesFournisseurs() {
  const [dettes,      setDettes]      = useState([]);
  const [fournisseurs,setFournisseurs]= useState([]);
  const [loading,     setLoading]     = useState(true);
  const [submitting,  setSubmitting]  = useState(false);
  const [toast,       setToast]       = useState(null);

  const [filters, setFilters] = useState({
    fournisseur: '', statutPaiement: '', dateDebut: '', dateFin: ''
  });

  const [showModal,     setShowModal]     = useState(false);
  const [selectedDette, setSelectedDette] = useState(null);
  const [paymentData,   setPaymentData]   = useState({
    montant: '', modePaiement: 'especes', commentaire: ''
  });
  const [expandedRows,  setExpandedRows]  = useState({});
  const [pdfLoading,    setPdfLoading]    = useState({});

  const totalDette    = useMemo(() => dettes.reduce((s, d) => s + Number(d.resteAPayer || 0), 0), [dettes]);
  const totalCommande = useMemo(() => dettes.reduce((s, d) => s + Number(d.montantTotal || 0), 0), [dettes]);
  const nbNonPayees   = dettes.filter(d => d.statutPaiement === 'non_payee').length;
  const nbPartielles  = dettes.filter(d => d.statutPaiement === 'partielle').length;

  useEffect(() => { loadData(); }, [filters]); // eslint-disable-line

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.fournisseur)    params.fournisseur    = filters.fournisseur;
      if (filters.statutPaiement) params.statutPaiement = filters.statutPaiement;
      if (filters.dateDebut)      params.dateDebut      = filters.dateDebut;
      if (filters.dateFin)        params.dateFin        = filters.dateFin;
      const [dr, fr] = await Promise.all([getDettesFournisseurs(params), getFournisseurs(true)]);
      setDettes(dr.data.data || []);
      setFournisseurs(fr.data.data || []);
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => setFilters({ fournisseur: '', statutPaiement: '', dateDebut: '', dateFin: '' });

  const openModal = (d) => {
    setSelectedDette(d);
    setPaymentData({ montant: d.resteAPayer || '', modePaiement: 'especes', commentaire: '' });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedDette(null);
    setPaymentData({ montant: '', modePaiement: 'especes', commentaire: '' });
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!selectedDette) return;
    const montant = Number(paymentData.montant || 0);
    if (montant <= 0) { showToast('error', 'Le montant doit être supérieur à 0'); return; }
    if (montant > Number(selectedDette.resteAPayer || 0)) {
      showToast('error', `Le montant dépasse le reste à payer (${fmt(selectedDette.resteAPayer)})`);
      return;
    }
    setSubmitting(true);
    try {
      await payerCommandeFournisseur(selectedDette._id, {
        montant, modePaiement: paymentData.modePaiement, commentaire: paymentData.commentaire
      });
      showToast('success', 'Paiement enregistré avec succès');
      closeModal();
      loadData();
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur lors du paiement');
    } finally {
      setSubmitting(false);
    }
  };

  const resteApres = Math.max(Number(selectedDette?.resteAPayer || 0) - Number(paymentData.montant || 0), 0);

  const toggleRow = (id) => setExpandedRows(p => ({ ...p, [id]: !p[id] }));

  const ouvrirBon = async (commandeId, paiementId) => {
    const key = `${commandeId}-${paiementId}`;
    setPdfLoading(p => ({ ...p, [key]: true }));
    try {
      const res = await getBonPaiementFournisseurPDF(commandeId, paiementId);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch { showToast('error', 'Erreur lors de la génération du bon'); }
    finally { setPdfLoading(p => ({ ...p, [key]: false })); }
  };

  if (loading) return (
    <div className="df-loading"><Loader className="df-spinner" size={26}/><span>Chargement…</span></div>
  );

  return (
    <div className="df-page">

      {/* Toast */}
      {toast && (
        <div className={`df-toast df-toast--${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={13}/> : <AlertCircle size={13}/>}
          {toast.text}
        </div>
      )}

      {/* ── Header ── */}
      <div className="df-header">
        <div className="df-header-left">
          <div className="df-header-icon"><CreditCard size={20}/></div>
          <div>
            <h1 className="df-header-title">Dettes Fournisseurs</h1>
            <p className="df-header-sub">Suivi des commandes reçues non totalement payées</p>
          </div>
        </div>
        <div className="df-header-debt">
          <span className="df-header-debt-label">Encours total</span>
          <span className="df-header-debt-amount">{fmt(totalDette)}</span>
        </div>
      </div>

      <div className="df-content">

        {/* KPI row */}
        <div className="df-kpi-row">
          {[
            { label: 'Commandes',   value: dettes.length,    color: '#6366f1', bg: '#eef2ff',  icon: Wallet },
            { label: 'Non payées',  value: nbNonPayees,       color: '#dc2626', bg: '#fee2e2',  icon: AlertCircle },
            { label: 'Partielles',  value: nbPartielles,      color: '#d97706', bg: '#fef3c7',  icon: Clock },
            { label: 'Total cmds',  value: fmt(totalCommande),color: '#64748b', bg: '#f1f5f9',  icon: TrendingDown },
            { label: 'Reste à payer',value: fmt(totalDette),  color: '#ef4444', bg: '#fef2f2',  icon: DollarSign },
          ].map((k, i) => {
            const Icon = k.icon;
            return (
              <div key={i} className="df-kpi" style={{ borderTopColor: k.color }}>
                <div className="df-kpi-icon" style={{ background: k.bg, color: k.color }}><Icon size={14}/></div>
                <div className="df-kpi-val">{k.value}</div>
                <div className="df-kpi-label">{k.label}</div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="df-filters">
          <Filter size={13} style={{ color: '#94a3b8', flexShrink: 0 }}/>
          <select className="df-select" value={filters.fournisseur}
            onChange={e => setFilters({...filters, fournisseur: e.target.value})}>
            <option value="">Tous les fournisseurs</option>
            {fournisseurs.map(f => <option key={f._id} value={f._id}>{f.nom}</option>)}
          </select>
          <select className="df-select" value={filters.statutPaiement}
            onChange={e => setFilters({...filters, statutPaiement: e.target.value})}>
            <option value="">Tous les statuts</option>
            <option value="non_payee">Non payée</option>
            <option value="partielle">Partielle</option>
          </select>
          <input className="df-date-input" type="date" value={filters.dateDebut}
            onChange={e => setFilters({...filters, dateDebut: e.target.value})}
            title="Date début"/>
          <input className="df-date-input" type="date" value={filters.dateFin}
            onChange={e => setFilters({...filters, dateFin: e.target.value})}
            title="Date fin"/>
          <button className="df-reset-btn" onClick={resetFilters} title="Réinitialiser">
            <RotateCcw size={13}/> Réinitialiser
          </button>
          <span className="df-count">{dettes.length} résultat{dettes.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Table */}
        {dettes.length === 0 ? (
          <div className="df-empty">
            <div className="df-empty-icon"><CheckCircle size={32}/></div>
            <h3>Aucune dette en cours</h3>
            <p>Tous les fournisseurs sont à jour</p>
          </div>
        ) : (
          <div className="df-card">
            <table className="df-table">
              <thead>
                <tr>
                  <th>Fournisseur</th>
                  <th>N° Commande</th>
                  <th>Date</th>
                  <th>Total commande</th>
                  <th>Déjà payé</th>
                  <th>Reste à payer</th>
                  <th>Statut</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {dettes.map(d => {
                  const isOpen = expandedRows[d._id];
                  const paiements = d.paiements || [];
                  return (
                    <React.Fragment key={d._id}>
                      <tr className={isOpen ? 'df-row--open' : ''}>
                        <td>
                          <div className="df-supplier-cell">
                            <div className="df-supplier-av">{d.fournisseur?.nom?.charAt(0) || 'F'}</div>
                            <div>
                              <div className="df-supplier-name">{d.fournisseur?.nom || '—'}</div>
                              {d.fournisseur?.telephone && (
                                <div className="df-supplier-tel">{d.fournisseur.telephone}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td><span className="df-num">{d.numeroCommande}</span></td>
                        <td className="df-date">{fdate(d.dateCommande)}</td>
                        <td className="df-amount">{fmt(d.montantTotal)}</td>
                        <td className="df-amount df-amount--paid">{fmt(d.montantPaye)}</td>
                        <td><span className="df-reste">{fmt(d.resteAPayer)}</span></td>
                        <td><StatutBadge statut={d.statutPaiement}/></td>
                        <td>
                          <div className="df-row-actions">
                            <button className="df-pay-btn" onClick={() => openModal(d)}>
                              <CreditCard size={12}/> Payer
                            </button>
                            <button
                              className={`df-history-btn${isOpen ? ' df-history-btn--open' : ''}`}
                              onClick={() => toggleRow(d._id)}
                              title="Voir l'historique des paiements">
                              {isOpen ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}
                              <span>{paiements.length}</span>
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* ── Historique des paiements ── */}
                      {isOpen && (
                        <tr className="df-history-row">
                          <td colSpan="8" className="df-history-cell">
                            {paiements.length === 0 ? (
                              <div className="df-history-empty">
                                <FileText size={16}/> Aucun paiement enregistré pour cette commande.
                              </div>
                            ) : (
                              <div className="df-history-wrap">
                                <div className="df-history-title">
                                  <FileText size={13}/> Historique des paiements — {paiements.length} paiement{paiements.length > 1 ? 's' : ''}
                                </div>
                                <table className="df-history-table">
                                  <thead>
                                    <tr>
                                      <th>#</th>
                                      <th>Date</th>
                                      <th>Montant</th>
                                      <th>Mode</th>
                                      <th>Commentaire</th>
                                      <th>Bon</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {paiements.map((p, i) => {
                                      const key = `${d._id}-${p._id}`;
                                      const isLoadingPdf = pdfLoading[key];
                                      return (
                                        <tr key={p._id}>
                                          <td className="df-history-idx">{i + 1}</td>
                                          <td className="df-date">{fdate(p.datePaiement)}</td>
                                          <td className="df-history-amount">{fmt(p.montant)}</td>
                                          <td className="df-history-mode">{MODES[p.modePaiement] || p.modePaiement}</td>
                                          <td className="df-history-note">{p.commentaire || <span className="df-muted">—</span>}</td>
                                          <td>
                                            <button
                                              className="df-bon-btn"
                                              onClick={() => ouvrirBon(d._id, p._id)}
                                              disabled={isLoadingPdf}
                                              title="Télécharger le bon de paiement">
                                              {isLoadingPdf
                                                ? <Loader size={12} className="df-spin"/>
                                                : <><Download size={12}/> Bon</>}
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>

              {/* Totals footer */}
              <tfoot>
                <tr className="df-tfoot">
                  <td colSpan="4" className="df-tfoot-label">Totaux ({dettes.length} commandes)</td>
                  <td className="df-amount df-amount--paid">{fmt(dettes.reduce((s,d) => s + Number(d.montantPaye||0), 0))}</td>
                  <td><span className="df-reste df-reste--total">{fmt(totalDette)}</span></td>
                  <td colSpan="2"/>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* ── Modal Paiement ── */}
      {showModal && selectedDette && (
        <>
          <div className="df-overlay" onClick={closeModal}/>
          <div className="df-modal">
            <div className="df-modal-head">
              <div>
                <h2 className="df-modal-title"><CreditCard size={15}/> Enregistrer un paiement</h2>
                <p className="df-modal-sub">{selectedDette.fournisseur?.nom} · {selectedDette.numeroCommande}</p>
              </div>
              <button className="df-modal-close" onClick={closeModal}><X size={16}/></button>
            </div>

            <form onSubmit={handlePayment} className="df-modal-body">

              {/* Info summary */}
              <div className="df-pay-summary">
                <div className="df-pay-row">
                  <span>Total commande</span>
                  <strong>{fmt(selectedDette.montantTotal)}</strong>
                </div>
                <div className="df-pay-row">
                  <span>Déjà payé</span>
                  <strong className="df-paid">{fmt(selectedDette.montantPaye)}</strong>
                </div>
                <div className="df-pay-row df-pay-row--owed">
                  <span>Reste à payer</span>
                  <strong>{fmt(selectedDette.resteAPayer)}</strong>
                </div>
              </div>

              {/* Payment form */}
              <div className="df-form-row">
                <div className="df-field">
                  <label>Montant payé (XOF) *</label>
                  <input type="number" min="1" max={selectedDette.resteAPayer}
                    value={paymentData.montant} required
                    onChange={e => setPaymentData({...paymentData, montant: e.target.value})}
                    autoFocus/>
                </div>
                <div className="df-field">
                  <label>Mode de paiement</label>
                  <select value={paymentData.modePaiement}
                    onChange={e => setPaymentData({...paymentData, modePaiement: e.target.value})}>
                    <option value="especes">Espèces</option>
                    <option value="wave">Wave</option>
                    <option value="orange_money">Orange Money</option>
                    <option value="carte_bancaire">Carte bancaire</option>
                    <option value="virement">Virement</option>
                    <option value="cheque">Chèque</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
              </div>

              <div className="df-field">
                <label>Commentaire <span className="df-opt">(optionnel)</span></label>
                <textarea rows="2" value={paymentData.commentaire}
                  onChange={e => setPaymentData({...paymentData, commentaire: e.target.value})}
                  placeholder="Note sur ce paiement…"/>
              </div>

              {/* After-payment preview */}
              {paymentData.montant > 0 && (
                <div className="df-after-pay">
                  <span>Reste après ce paiement</span>
                  <strong className={resteApres === 0 ? 'df-zero' : 'df-still-owed'}>
                    {fmt(resteApres)}
                    {resteApres === 0 && ' ✓ Soldé'}
                  </strong>
                </div>
              )}

              <div className="df-modal-footer">
                <button type="button" className="df-btn-cancel" onClick={closeModal}>Annuler</button>
                <button type="submit" className="df-btn-confirm" disabled={submitting}>
                  {submitting
                    ? <><Loader size={13} className="df-spin"/> Enregistrement…</>
                    : <><Check size={13}/> Enregistrer le paiement</>}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
